import { Stack, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsiveText } from '../../hooks';
import { getDateFormatLocale } from '../../util';
import { FormatTimestamp } from '../format';
import { DateTimeTooltip } from '../tooltip';

export const DateDisplay: FC<{ date: Date; timezone: string; 'data-testid'?: string }> = ({ date, timezone, ...props }): JSX.Element => {
  const dateTimeObject = DateTime.fromJSDate(date, { zone: timezone }).setLocale(getDateFormatLocale());
  const dateStamp = dateTimeObject.toJSDate();
  const whenLocal = dateTimeObject.toLocal();
  const { t } = useTranslation(['common']);
  const localTimeFormat = dateTimeObject.day !== whenLocal.day ? DateTime.DATETIME_MED_WITH_SECONDS : DateTime.TIME_WITH_SECONDS;
  const localTimeString = dateTimeObject.toLocaleString({ ...localTimeFormat });

  const captionStyles = useResponsiveText('0.75rem', 15, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 15,
  });

  return (
    <Stack data-chromatic="ignore" data-testid={props['data-testid']}>
      <DateTimeTooltip
        variant='body2'
        disableHoverListener={whenLocal.offsetNameShort === dateTimeObject.offsetNameShort}
        display={
          <Box data-testid="date-time" component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
            <span><FormatTimestamp format="dateFull" value={dateStamp} /></span>
            <span><FormatTimestamp format="timeWithSeconds" value={dateStamp} /></span>
          </Box>
        }
        placement="top"
        title={t('common:component.alert-table.hint.tooltip', { dateTime: localTimeString, offsetName: dateTimeObject.offsetNameShort })}
      />
      <Typography
        data-testid="relative-time"
        color="text.secondary"
        variant="caption"
        style={{
          fontSize: captionStyles.fontSize,
          lineHeight: captionStyles.lineHeight,
          wordBreak: captionStyles.wordBreak,
          whiteSpace: captionStyles.whiteSpace,
          overflow: captionStyles.overflow,
          textOverflow: captionStyles.textOverflow,
        }}
      >
        <FormatTimestamp format="relative" value={dateStamp} />
      </Typography>
    </Stack>
  );
};
